import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik";
import { Pencil, Plus, Trash2, List, Network } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import * as Accordion from "@radix-ui/react-accordion";
import SideDrawer from "@/components/ui/SideDrawer";
import WelcomeView from "@/components/ui/WelcomeView";
import { entityService } from "@/services/breeze/EntityService";
import { IEntity, CreateEntity, UpdateEntity } from "@/types/breeze/Entity";
import InputField from "@/components/form/InputField";
import { useEntities } from "@/hooks/useCommonQueries";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import DataModelEditor from "@/components/data-model/DataModelEditor";
import SplitView from "@/components/ui/SplitView";
import CheckboxField from "@/components/form/CheckboxField";
import PageTemplate from "@/components/page-template/PageTemplate";
import {
  Text,
  Box,
  Flex,
  Button,
  Callout,
  Badge,
  SegmentedControl,
} from "@radix-ui/themes";
import Surface from "@/components/ui/Surface";
import { SortableList, SortableItem, DragHandle } from "@/components/dnd";
import { useAuth } from "@/providers/AuthProvider";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import EntityERD from "@/components/data-model/EntityERD";
import { IconPicker } from "@/components/ui/IconPicker";

const AccordionTriggerStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-2)",
  borderRadius: "var(--radius-2)",
  backgroundColor: "transparent",
  cursor: "pointer",
  transition: "background-color 0.2s",
  ":hover": {
    backgroundColor: "var(--gray-a3)",
  },
  '[dataState="open"] > svg': {
    transform: "rotate(180deg)",
  },
  "& > svg": {
    transition: "transform 300ms",
  },
} as const;

const validationSchema = Yup.object({
  name: Yup.string()
    .required("This field is required.")
    .matches(
      /^(?!anythink_)[a-zA-Z][a-zA-Z0-9_]*$/,
      "Must contain only letters, numbers, and underscores, must start with a letter, and cannot start with 'anythink_'",
    )
    .notOneOf(
      [
        "documentation",
        "entities",
        "fields",
        "files",
        "menus",
        "modules",
        "permissions",
        "roles",
        "secrets",
        "users",
        "workflows",
      ],
      "This name is reserved and cannot be used.",
    ),
  add_to_my_menu_automatically: Yup.boolean(),
  menu_icon: Yup.string().when("add_to_my_menu_automatically", {
    is: true,
    then: (schema) =>
      schema.required("Menu icon is required when this option is enabled."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const DataModelPage = withTenantAdminRequired(function DataModelPage() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"create" | "edit">("create");
  const [selectedEntity, setSelectedEntity] = useState<IEntity | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "erd">("list");
  const queryClient = useQueryClient();
  const { currentTenant, safeNavigate } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedEntityName = searchParams.get("entityName");
  const confirm = useConfirm();

  const { data: entities, isLoading, error } = useEntities(true);

  const handleEntitySelect = useCallback(
    (entityName: string) => {
      safeNavigate(
        `/org/${currentTenant?.id}/settings/data-model?entityName=${entityName}`,
      );
    },
    [safeNavigate, currentTenant?.id],
  );

  // Find the selected entity from the entities list
  useEffect(() => {
    if (selectedEntityName && entities) {
      const entity = entities.find((e) => e.name === selectedEntityName);
      if (entity) {
        setSelectedEntity(entity);
      }
    }
  }, [selectedEntityName, entities]);

  const createEntity = useMutation({
    mutationFn: (data: CreateEntity) => entityService.createEntity(data),
    onSuccess: (created, variables) => {
      queryClient.invalidateQueries({ queryKey: ["entities"] });
      queryClient.invalidateQueries({
        queryKey: ["entity", selectedEntityName],
      });
      queryClient.invalidateQueries({ queryKey: ["my-menu"] });
      setIsDrawerOpen(false);
      handleEntitySelect(created?.name ?? variables.name);
    },
  });

  const updateEntity = useMutation({
    mutationFn: ({ name, data }: { name: string; data: UpdateEntity }) =>
      entityService.updateEntity(name, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entities"] });
      queryClient.invalidateQueries({
        queryKey: ["entity", selectedEntityName],
      });
      setIsDrawerOpen(false);
    },
  });

  const deleteEntity = useMutation({
    mutationFn: (entityName: string) => entityService.deleteEntity(entityName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entities"] });
      safeNavigate(`/org/${currentTenant?.id}/settings/data-model`);
    },
  });

  const createInitialValues: CreateEntity = {
    name: "",
    enable_rls: false,
    lock_new_records: false,
    is_public: false,
    locked: false,
    add_to_my_menu_automatically: false,
    menu_icon: "",
  };

  const updateInitialValues: UpdateEntity = useMemo(() => {
    return {
      name: selectedEntity?.name || "",
      enable_rls: selectedEntity?.enable_rls || false,
      lock_new_records: selectedEntity?.lock_new_records || false,
      locked: selectedEntity?.locked || false,
      is_public: selectedEntity?.is_public || false,
    };
  }, [selectedEntity]);

  const handleDeleteEntity = async (entity: IEntity) => {
    const confirmed = await confirm({
      title: "Delete Entity",
      message: `Are you sure you want to delete the entity "${entity.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      deleteEntity.mutate(entity.name);
    }
  };

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setIsDrawerOpen(true);
  };

  const openEditDrawer = () => {
    if (selectedEntity) {
      setDrawerMode("edit");
      setIsDrawerOpen(true);
    }
  };

  const reorderEntitiesMutation = useMutation({
    mutationFn: (reordered: IEntity[]) =>
      entityService.reorderEntities(
        reordered.map((e, i) => ({ entity_id: e.id, sort_order: i })),
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["entities"] });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["entities"] }),
  });

  // User-created entities sit in the reorderable main list.
  const mainEntities = useMemo(
    () =>
      entities
        ?.filter((entity) => !entity.is_junction && !entity.is_system)
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            a.name.localeCompare(b.name),
        ) ?? [],
    [entities],
  );

  // Unlocked system entities (user_profiles today) render as their own section
  // — visible + inspectable, but not reorderable and locked ones stay hidden.
  const systemEntities = useMemo(
    () =>
      entities
        ?.filter((entity) => entity.is_system && !entity.locked)
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [entities],
  );

  // Default selection must mirror the sidebar, not the raw API order — otherwise it can land on a hidden, locked entity.
  useEffect(() => {
    // Don't auto-select if drawer is open to prevent closing it
    if (isDrawerOpen || selectedEntityName) return;

    const defaultEntity = mainEntities[0] ?? systemEntities[0];
    if (defaultEntity) {
      handleEntitySelect(defaultEntity.name);
    }
  }, [
    mainEntities,
    systemEntities,
    selectedEntityName,
    isDrawerOpen,
    handleEntitySelect,
  ]);

  const fieldCountLabel = (entity: IEntity) => {
    const total = entity.fields?.length ?? 0;
    if (!entity.is_system) return `${total} fields`;
    const system = entity.fields?.filter((f) => f.is_system).length ?? 0;
    const custom = total - system;
    return `${system} system · ${custom} custom`;
  };

  const junctionEntities = useMemo(
    () =>
      entities
        ?.filter((entity) => entity.is_junction)
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [entities],
  );

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  if (error) {
    return <div>Error loading entities</div>;
  }

  return (
    <PageTemplate
      title="Data Model"
      description="Design and manage your application's data structure. Create entities, define fields, and establish relationships between them."
      rightContent={
        <Flex gap="2" align="center">
          <SegmentedControl.Root
            value={viewMode}
            onValueChange={(value) => setViewMode(value as "list" | "erd")}
          >
            <SegmentedControl.Item value="list">
              <Flex align="center" gap="1">
                <List style={{ width: "14px", height: "14px" }} />
                List
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="erd">
              <Flex align="center" gap="1">
                <Network style={{ width: "14px", height: "14px" }} />
                Diagram
              </Flex>
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>
      }
    >
      {viewMode === "erd" ? (
        <Box style={{ height: "calc(100vh - 250px)" }}>
          {entities && entities.length > 0 ? (
            <EntityERD
              entities={entities}
              onEntityClick={(entityName) => {
                setViewMode("list");
                handleEntitySelect(entityName);
              }}
            />
          ) : (
            <Flex
              align="center"
              justify="center"
              style={{ height: "100%" }}
              direction="column"
              gap="4"
            >
              <Network
                style={{
                  width: "48px",
                  height: "48px",
                  color: "var(--gray-a8)",
                }}
              />
              <Box style={{ textAlign: "center" }}>
                <Text size="4" weight="bold" mb="2">
                  No Entities Yet
                </Text>
                <Text size="2" color="gray" mb="4">
                  Create your first entity to see the ERD visualisation
                </Text>
                <Button onClick={openCreateDrawer}>
                  <Plus style={{ width: "16px", height: "16px" }} />
                  Create Entity
                </Button>
              </Box>
            </Flex>
          )}
        </Box>
      ) : (
        <SplitView>
          {/* Left Panel - Entity List */}
          <SplitView.Left>
            <Box pr="4">
              <Flex justify="between" align="center" mb="4">
                <Text size="4" weight="bold">
                  Entities
                </Text>
                <Button onClick={openCreateDrawer} size="1">
                  <Plus style={{ width: "16px", height: "16px" }} />
                  Add Entity
                </Button>
              </Flex>

              <Flex direction="column" gap="2">
                <SortableList
                  items={mainEntities}
                  getItemId={(e) => e.id}
                  onReorder={(reordered) =>
                    reorderEntitiesMutation.mutate(reordered)
                  }
                  renderItem={(entity) => (
                    <SortableItem key={entity.id} id={entity.id}>
                      {({ dragHandleProps }) => (
                        <Surface
                          preTitleContent={<DragHandle {...dragHandleProps} />}
                          title={entity.name}
                          body={fieldCountLabel(entity)}
                          selected={selectedEntityName === entity.name}
                          onClick={() => handleEntitySelect(entity.name)}
                          additionalTitleContent={
                            entity.enable_rls ? (
                              <Badge variant="soft">RLS</Badge>
                            ) : undefined
                          }
                          actions={[
                            {
                              label: "Edit",
                              icon: (
                                <Pencil
                                  style={{ width: "1rem", height: "1rem" }}
                                />
                              ),
                              onClick: () => openEditDrawer(),
                            },
                            {
                              label: "Delete",
                              icon: (
                                <Trash2
                                  style={{
                                    width: "1rem",
                                    height: "1rem",
                                    color: "var(--red-9)",
                                  }}
                                />
                              ),
                              onClick: () => handleDeleteEntity(entity),
                            },
                          ]}
                        />
                      )}
                    </SortableItem>
                  )}
                  renderDragOverlay={(entity) => (
                    <Surface
                      preTitleContent={<DragHandle />}
                      title={entity.name}
                      body={fieldCountLabel(entity)}
                    />
                  )}
                />

                {systemEntities.length > 0 && (
                  <Box mt="4">
                    <Accordion.Root type="single" collapsible defaultValue="system-tables">
                      <Accordion.Item value="system-tables">
                        <Accordion.Trigger style={AccordionTriggerStyles}>
                          <Flex align="center" gap="2">
                            <ChevronDown
                              style={{ width: "16px", height: "16px" }}
                            />
                            <Text size="2" color="gray" weight="medium">
                              System Tables ({systemEntities.length})
                            </Text>
                          </Flex>
                        </Accordion.Trigger>
                        <Accordion.Content>
                          <Box mt="2">
                            <Flex direction="column" gap="2">
                              {systemEntities.map((entity) => (
                                <Surface
                                  key={entity.name}
                                  title={entity.name}
                                  body={fieldCountLabel(entity)}
                                  selected={selectedEntityName === entity.name}
                                  onClick={() =>
                                    handleEntitySelect(entity.name)
                                  }
                                />
                              ))}
                            </Flex>
                          </Box>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  </Box>
                )}

                {junctionEntities.length > 0 && (
                  <Box mt="4">
                    <Accordion.Root type="single" collapsible>
                      <Accordion.Item value="junction-tables">
                        <Accordion.Trigger style={AccordionTriggerStyles}>
                          <Flex align="center" gap="2">
                            <ChevronDown
                              style={{ width: "16px", height: "16px" }}
                            />
                            <Text size="2" color="gray" weight="medium">
                              Junction Tables ({junctionEntities.length})
                            </Text>
                          </Flex>
                        </Accordion.Trigger>
                        <Accordion.Content>
                          <Box mt="2">
                            <Flex direction="column" gap="2">
                              {junctionEntities.map((entity) => (
                                <Surface
                                  key={entity.name}
                                  title={entity.name}
                                  body={fieldCountLabel(entity)}
                                  selected={selectedEntityName === entity.name}
                                  onClick={() =>
                                    handleEntitySelect(entity.name)
                                  }
                                />
                              ))}
                            </Flex>
                          </Box>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  </Box>
                )}
              </Flex>
            </Box>
          </SplitView.Left>

          {/* Right Panel - Entity Details */}
          <SplitView.Right>
            {mainEntities.length === 0 ? (
              <WelcomeView
                title="Start Here to Create Your App"
                description="Building a data model is easy with Anythink. Create entities, define fields, and establish relationships to bring your application to life."
                quickStartSteps={[
                  {
                    text: "Create your first entity (like User, Product, or Order)",
                  },
                  { text: "Add fields to define your data structure" },
                  { text: "Set up relationships between entities" },
                  { text: "Your API endpoints are automatically generated!" },
                ]}
                primaryAction={{
                  text: "Create Your First Entity",
                  icon: <Plus style={{ width: "16px", height: "16px" }} />,
                  onClick: openCreateDrawer,
                }}
                padding={{
                  top: "0",
                  left: "var(--space-6)",
                }}
              />
            ) : selectedEntityName ? (
              <>
                <Box>
                  {selectedEntity?.is_junction && (
                    <Flex mb="4" p="3">
                      <Callout.Root
                        color="orange"
                        role="alert"
                        style={{ width: "100%" }}
                      >
                        <Callout.Icon>
                          <AlertTriangle
                            style={{ width: "16px", height: "16px" }}
                          />
                        </Callout.Icon>
                        <Callout.Text>
                          <Text weight="medium">Warning: Junction Table</Text>
                          <br />
                          This is a system-generated junction table used for
                          many-to-many relationships. Modifying this table could
                          break your application&apos;s data integrity and
                          functionality. Please proceed with caution.
                        </Callout.Text>
                      </Callout.Root>
                    </Flex>
                  )}
                  {selectedEntity?.locked && (
                    <Flex mb="4" p="3">
                      <Callout.Root
                        color="orange"
                        role="alert"
                        style={{ width: "100%" }}
                      >
                        <Callout.Icon>
                          <AlertTriangle
                            style={{ width: "16px", height: "16px" }}
                          />
                        </Callout.Icon>
                        <Callout.Text>
                          <Text weight="medium">Warning: Schema Locked</Text>
                          <br />
                          This entity&apos;s schema is locked and no fields can
                          be added or modified. If you need to modify the
                          schema, please unlock the schema first. You may still
                          reorder the fields.
                        </Callout.Text>
                      </Callout.Root>
                    </Flex>
                  )}
                </Box>
                <DataModelEditor key={selectedEntityName} />
              </>
            ) : (
              <Flex align="center" justify="center" style={{ height: "100%" }}>
                <Text color="gray">Select an entity to view details</Text>
              </Flex>
            )}
          </SplitView.Right>
        </SplitView>
      )}

      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={drawerMode === "create" ? "Create New Entity" : "Edit Entity"}
        saveButton={{
          formId: "entity-form",
          label: drawerMode === "create" ? "Create Entity" : "Update Entity",
          loading:
            drawerMode === "create"
              ? createEntity.isPending
              : updateEntity.isPending,
        }}
      >
        <Formik<CreateEntity | UpdateEntity>
          initialValues={
            drawerMode === "create" ? createInitialValues : updateInitialValues
          }
          onSubmit={(values: CreateEntity | UpdateEntity) => {
            if (drawerMode === "create") {
              createEntity.mutate(values as CreateEntity);
            } else {
              updateEntity.mutate({
                name: selectedEntity?.name || "",
                data: values,
              });
            }
          }}
          validationSchema={validationSchema}
        >
          {({ values }) => (
            <Form
              id="entity-form"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-4)",
              }}
            >
              <InputField
                name="name"
                label="Entity Name"
                required
                disabled={drawerMode === "edit"}
              />

              <CheckboxField
                name="enable_rls"
                label="Enable Row-Level Security"
                description="If enabled, individual records can be protected so that only authorised users can view or modify them"
              />

              <CheckboxField
                name="is_public"
                label="Public"
                description="If enabled, the entity will be public in the API and can be accessed by anyone. Only fields marked as Publicly Searchable will be visible."
              />

              <CheckboxField
                name="lock_new_records"
                label="Lock New Records"
                description="If enabled, new records will be locked by default"
              />

              <CheckboxField
                name="locked"
                label="Lock Schema"
                description="If enabled, the schema will be locked and no fields can be added or modified"
                disabled={
                  drawerMode === "edit" &&
                  selectedEntity?.is_system === true &&
                  selectedEntity?.locked === true
                }
              />

              {drawerMode === "create" && (
                <>
                  <CheckboxField
                    name="add_to_my_menu_automatically"
                    label="Add to Menu"
                    description="If enabled, the entity will be added to the menu automatically"
                  />

                  {(values as CreateEntity).add_to_my_menu_automatically && (
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--space-2)",
                      }}
                    >
                      <Text as="label" size="2" weight="medium">
                        Icon
                      </Text>
                      <Field name="menu_icon">
                        {({ field, form }: FieldProps) => (
                          <IconPicker
                            value={field.value || ""}
                            onChange={(iconName: string) =>
                              form.setFieldValue("menu_icon", iconName)
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage name="menu_icon">
                        {(msg: string) => (
                          <Text size="1" color="red">
                            {msg}
                          </Text>
                        )}
                      </ErrorMessage>
                    </Box>
                  )}
                </>
              )}
            </Form>
          )}
        </Formik>
      </SideDrawer>
    </PageTemplate>
  );
});

export default DataModelPage;
